import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { defaultSettings } from '@/components/settings/config-setting';
import { getPresets, presetsOption } from '@/components/settings/presets';

// Theme values are stored as plain strings (they originate from JS config and
// from <select> change events), except the boolean stretch flag.
interface SettingsValues {
  themeMode: string;
  themeDirection: string;
  themeContrast: string;
  themeLayout: string;
  themeColorPresets: string;
  themeStretch: boolean;
}

// The onChange handlers only read `target.value`, so accept any element event.
type ChangeLike = { target: { value: string } };

export interface SettingsState extends SettingsValues {
  // Mode actions
  onToggleMode: () => void;
  onChangeMode: (event: ChangeLike) => void;

  // Direction actions
  onToggleDirection: () => void;
  onChangeDirection: (event: ChangeLike) => void;
  onChangeDirectionByLang: (lang: string) => void;

  // Layout actions
  onToggleLayout: () => void;
  onChangeLayout: (event: ChangeLike) => void;

  // Contrast actions
  onToggleContrast: () => void;
  onChangeContrast: (event: ChangeLike) => void;

  // Color presets action
  onChangeColorPresets: (event: ChangeLike) => void;

  // Stretch action
  onToggleStretch: () => void;

  // Reset action
  onResetSetting: () => void;

  // Computed values (accessed via selectors)
  presetsOption: typeof presetsOption;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      // State
      ...defaultSettings,

      // Mode actions
      onToggleMode: () =>
        set(state => ({
          themeMode: state.themeMode === 'light' ? 'dark' : 'light',
        })),

      onChangeMode: event => set({ themeMode: event.target.value }),

      // Direction actions
      onToggleDirection: () =>
        set(state => ({
          themeDirection: state.themeDirection === 'rtl' ? 'ltr' : 'rtl',
        })),

      onChangeDirection: event => set({ themeDirection: event.target.value }),

      onChangeDirectionByLang: lang => {
        const newDirection = lang === 'ar' ? 'rtl' : 'ltr';
        const currentDirection = get().themeDirection;
        if (currentDirection !== newDirection) {
          set({ themeDirection: newDirection });
        }
      },

      // Layout actions
      onToggleLayout: () =>
        set(state => ({
          themeLayout: state.themeLayout === 'vertical' ? 'mini' : 'vertical',
        })),

      onChangeLayout: event => set({ themeLayout: event.target.value }),

      // Contrast actions
      onToggleContrast: () =>
        set(state => ({
          themeContrast: state.themeContrast === 'default' ? 'bold' : 'default',
        })),

      onChangeContrast: event => set({ themeContrast: event.target.value }),

      // Color presets actions
      onChangeColorPresets: event =>
        set({ themeColorPresets: event.target.value }),

      // Stretch action
      onToggleStretch: () =>
        set(state => ({ themeStretch: !state.themeStretch })),

      // Reset action
      onResetSetting: () => set(defaultSettings),

      // Computed values (accessed via selectors)
      presetsOption,
    }),
    {
      name: 'settings',
      partialize: state => ({
        themeMode: state.themeMode,
        themeDirection: state.themeDirection,
        themeContrast: state.themeContrast,
        themeLayout: state.themeLayout,
        themeColorPresets: state.themeColorPresets,
        themeStretch: state.themeStretch,
      }),
    },
  ),
);

// Selector for presetsColor (computed from themeColorPresets)
export const usePresetsColor = () =>
  useSettingsStore(state => getPresets(state.themeColorPresets));

// Hook for backwards compatibility with useSettingsContext
export const useSettingsContext = () => {
  const store = useSettingsStore();
  return {
    ...store,
    presetsColor: getPresets(store.themeColorPresets),
  };
};
